export default class LaneAutoscroll {
	/**
	 * Handles automatic scrolling of the lane.
	 *
	 * @property {import('vue').Component} component - The Vue component instance.
	 * @property {GSAPTween|null} autoscrollTween - Current GSAP tween for autoscroll.
	 * @property {boolean} autoScrollStop - Indicates if autoscroll is manually stopped.
	 * @method startAutoscroll - Starts the autoscroll animation.
	 * @method stopAutoscroll - Stops the autoscroll animation.
	 * @method toggleAutoscroll - Toggles autoscroll on/off.
	 * @method handleVisibilityChange - Called when lane visibility changes.
	 * @method pauseAutoscroll - Pauses autoscroll (used on mouseover).
	 * @method resumeAutoscroll - Resumes autoscroll (used on mouseout).
	 * @method onMouseOver - Mouseover handler to pause autoscroll.
	 * @method onMouseOut - Mouseout handler to resume autoscroll.
	 */
	constructor(component) {
		this.component = component;
		this.autoscrollTween = null;
		this.autoScrollStop = false;

		// Mouseover/Mouseout nur binden, wenn `autoScrollItemHoverPause` aktiv
		this.onMouseOver = this.onMouseOver.bind(this);
		this.onMouseOut = this.onMouseOut.bind(this);
	}

	startAutoscroll() {
		const { lane, autoScrollSpeed, orientation, autoScrollItemHoverPause } = this.component;

		if (this.autoscrollTween) this.autoscrollTween.kill();
		if (this.autoScrollStop) return;

		// Falls hover-basierte Pause gewünscht ist, Event-Listener hinzufügen
		if (autoScrollItemHoverPause) {
			lane.addEventListener('mouseover', this.onMouseOver, true);
			lane.addEventListener('mouseout', this.onMouseOut, true);
		}

		if (orientation === 'vertical') {
			const laneHeight = lane.scrollHeight;
			const viewportHeight = lane.clientHeight;
			const maxScrollTop = laneHeight - viewportHeight;
			if (maxScrollTop <= 0) return;

			const scrollDuration = (maxScrollTop - lane.scrollTop) / autoScrollSpeed;
			this.autoscrollTween = gsap.to(lane, {
				scrollTo: { y: maxScrollTop, autoKill: true },
				duration: scrollDuration,
				ease: 'none',
			});
		} else {
			const laneWidth = lane.scrollWidth;
			const viewportWidth = lane.clientWidth;
			const maxScrollLeft = laneWidth - viewportWidth;
			if (maxScrollLeft <= 0) return;

			const scrollDuration = (maxScrollLeft - lane.scrollLeft) / autoScrollSpeed;
			this.autoscrollTween = gsap.to(lane, {
				scrollTo: { x: maxScrollLeft, autoKill: true },
				duration: scrollDuration,
				ease: 'none',
			});
		}
	}

	stopAutoscroll() {
		const { lane, autoScrollItemHoverPause } = this.component;

		if (this.autoscrollTween) {
			this.autoscrollTween.kill();
			console.log("stopAutoscroll");
			this.autoscrollTween = null;
		}

		// Event-Listener wieder entfernen
		if (autoScrollItemHoverPause) {
			lane.removeEventListener('mouseover', this.onMouseOver, true);
			lane.removeEventListener('mouseout', this.onMouseOut, true);
		}
	}

	toggleAutoscroll() {
		this.autoScrollStop = !this.autoScrollStop;
		if (this.autoScrollStop) {
			this.stopAutoscroll();
		} else {
			this.startAutoscroll();
		}
	}

	handleVisibilityChange(isVisible) {
		if (isVisible) {
			if (this.component.autoScroll && !this.autoScrollStop) {
				this.startAutoscroll();
			}
		} else {
			this.stopAutoscroll();
		}
	}

	pauseAutoscroll() {
		if (this.autoscrollTween && !this.autoScrollStop) {
			this.autoscrollTween.pause();
		}
	}

	resumeAutoscroll() {
		if (!this.autoScrollStop) {
			// Einfach neu starten (damit es ggf. von Anfang an korrekt berechnet wird)
			this.startAutoscroll();
		}
	}

	// Wird bei Mausüberfahrt aufgerufen
	onMouseOver() {
		this.component.autoScrollItemIsPausedMouseOver = true;
		this.pauseAutoscroll();
	}

	// Wird beim Verlassen aufgerufen
	onMouseOut() {
		this.component.autoScrollItemIsPausedMouseOver = false;
		this.resumeAutoscroll();
	}
}
